import React, { useState } from "react";
import { ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PolicyAgreement } from "@/components/PolicyAgreement";

export const AccountConsentBanner = ({ raisedForCookieConsent = false }) => {
  const { user, logout, updateCurrentUser } = useAuth();
  const [accepted, setAccepted] = useState(false);
  const [saving, setSaving] = useState(false);

  if (!user || user.role !== "customer" || !user.consent_required) {
    return null;
  }

  const accept = async () => {
    if (!accepted) {
      toast.error("Please review and accept the terms and privacy policy.");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/me/consent", { accepted: true });
      updateCurrentUser(data.user);
      toast.success("Your account consent has been saved.");
    } catch (error) {
      toast.error(formatError(error));
    } finally {
      setSaving(false);
    }
  };

  return (
    <aside
      className={[
        "fixed inset-x-0 z-[70] border-t border-[#D9C3A5] bg-[#FDFBF7]",
        "shadow-[0_-14px_38px_rgba(30,42,36,0.14)]",
      ].join(" ")}
      style={{ bottom: raisedForCookieConsent ? "88px" : "0" }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="account-consent-title"
      data-testid="account-consent-banner"
    >
      <div
        className={[
          "mx-auto flex max-w-4xl flex-col gap-4 px-4 py-5 sm:px-8",
          "md:flex-row md:items-center",
        ].join(" ")}
      >
        <ShieldCheck
          size={26}
          className="shrink-0 text-[#2D5A47]"
          aria-hidden="true"
        />
        <div className="min-w-0 flex-1">
          <h2
            id="account-consent-title"
            className="font-serif text-xl font-semibold text-[#1E2A24]"
          >
            Please review our customer terms
          </h2>
          <p className="mt-1 text-sm text-[#64748B]">
            Confirm your agreement before continuing to use your Eira account.
          </p>
          <div className="mt-3">
            <PolicyAgreement
              checked={accepted}
              onChange={setAccepted}
              testIdPrefix="account-consent"
            />
          </div>
        </div>
        <div className="flex shrink-0 flex-wrap gap-2 md:flex-col">
          <button
            type="button"
            onClick={accept}
            disabled={!accepted || saving}
            className={[
              "bg-[#2D5A47] px-4 py-2.5 text-sm font-semibold text-white",
              "hover:bg-[#214435] disabled:cursor-not-allowed disabled:opacity-50",
            ].join(" ")}
            data-testid="account-consent-accept"
          >
            {saving ? "Saving…" : "Accept and continue"}
          </button>
          <button
            type="button"
            onClick={logout}
            disabled={saving}
            className={[
              "px-4 py-2.5 text-sm font-semibold text-[#64748B]",
              "hover:text-[#1E2A24] disabled:opacity-50",
            ].join(" ")}
            data-testid="account-consent-logout"
          >
            Log out
          </button>
        </div>
      </div>
    </aside>
  );
};